import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, useAnimation } from "framer-motion";
import { Star, ChevronRight, Quote, MessageSquare, ThumbsUp, ChevronDown, MousePointer, Hand, Fingerprint } from "lucide-react";
import "./theme.css";

gsap.registerPlugin(ScrollTrigger);

const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const marqueeRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const controls = useAnimation();
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const GOOGLE_MAPS_LINK = "https://maps.app.goo.gl/mfQxTonXebrLAmw98";

  // Check for mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const sampleReviews = [
    {
      id: 1,
      author_name: "Kousik Bera",
      rating: 5,
      text: "Laminance kitchen did an exceptional job in making our dreams of a unique kind of kitchen in USA. We wanted the European style kitchen and not the traditional shaker style kitchen. He made that possible. Laminance has accommodated all our needs and our dream kitchen speaks for itself. The attention to detail and quality of craftsmanship exceeded our expectations. The team was professional, punctual, and kept the workspace clean throughout the project.",
      relative_time_description: "2 weeks ago",
      profile_photo_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
      project: "Modern Kitchen Remodel",
      location: "New Jersey"
    },
    {
      id: 2,
      author_name: "Abhay Kulkarni",
      rating: 5,
      text: "I got introduced to this business while at an event. I found the folks very approachable, not at all pushy or sales men like, rather professional, listening to the folks and working with them per their needs, making suggestions where necessary with proper reasoning for each. I got feedback from other clients that the quality and workmanship is topnotch. Vivek and his team take good care of their clients. I will highly recommend Laminance Cabinetry to anyone looking for quality kitchen solutions.",
      relative_time_description: "1 month ago",
      profile_photo_url: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop&crop=face",
      project: "Contemporary Kitchen",
      location: "New Jersey"
    },
    {
      id: 3,
      author_name: "Vikalp Chouhan",
      rating: 5,
      text: "Laminance Cabinetry are one of the best in NJ area. Best service, quality and looks. They have very competitive prices and are a must visit shop if you are looking for Kitchen upgrades. Their showroom displays various styles and finishes that helped us visualize our final kitchen design perfectly.",
      relative_time_description: "3 weeks ago",
      profile_photo_url: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      project: "Complete Kitchen Makeover",
      location: "New Jersey"
    },
  ];

  // Auto-scroll animation
  useEffect(() => {
    let animation;
    
    if (isAutoPlaying && !isDragging && !isHovering && marqueeRef.current && !isMobile) {
      const containerWidth = marqueeRef.current.scrollWidth / 2;
      
      animation = controls.start({
        x: -containerWidth,
        transition: {
          duration: 40,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        }
      });
    } else {
      controls.stop();
    }

    return () => {
      if (animation) {
        controls.stop();
      }
    };
  }, [isAutoPlaying, isDragging, isHovering, controls, isMobile]);

  // GSAP animations for entrance
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".section-title", {
        opacity: 0,
        y: 30,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".section-title",
          start: "top 85%",
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            size={isMobile ? 12 : 16}
            className={`${i < rating ? "fill-current text-yellow-400" : "text-gray-300"}`}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  };

  const TruncatedText = ({ text, reviewId }) => {
    const isExpanded = expandedReviews[reviewId];
    const maxLength = isMobile ? 80 : 150;
    
    if (text.length <= maxLength) {
      return <p className="text-gray-600 leading-relaxed text-sm">"{text}"</p>;
    }

    const truncatedText = isExpanded ? text : `${text.substring(0, maxLength)}...`;

    return (
      <div>
        <p className="text-gray-600 leading-relaxed text-sm">"{truncatedText}"</p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleReviewExpansion(reviewId);
          }}
          className="mt-2 flex items-center gap-1 text-xs font-medium transition-colors"
          style={{ color: "var(--color-primary-400)" }}
          aria-label={isExpanded ? "Read less" : "Read more"}
        >
          <span>{isExpanded ? "Read less" : "Read more"}</span>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDown size={12} />
          </motion.div>
        </button>
      </div>
    );
  };

  // Handle drag/pause controls
  const handleInteractionStart = () => {
    setIsDragging(true);
    setIsAutoPlaying(false);
  };

  const handleInteractionEnd = () => {
    setIsDragging(false);
    setTimeout(() => {
      if (!isHovering) {
        setIsAutoPlaying(true);
      }
    }, 3000);
  };

  // Duplicate reviews for seamless scrolling
  const duplicatedReviews = [...sampleReviews, ...sampleReviews];

  // Calculate aggregate rating for schema
  const totalRating = sampleReviews.reduce((sum, review) => sum + review.rating, 0);
  const averageRating = (totalRating / sampleReviews.length).toFixed(1);
  const totalReviews = sampleReviews.length;

  // Schema for testimonials
  const testimonialsSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",

  "name": "Customer Testimonials | Laminance Cabinetry",

  "description":
    "Read authentic customer reviews and testimonials for Laminance Cabinetry's custom kitchen cabinet services in New Jersey. 5-star rated kitchen renovation specialists.",

  "mainEntity": {
    "@type": "Service",

    "name": "Custom Kitchen Cabinet Installation & Renovation",

    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue":
        sampleReviews.length
          ? (
              sampleReviews.reduce(
                (sum, review) => sum + Number(review.rating || 0),
                0
              ) / sampleReviews.length
            ).toFixed(1)
          : "5",

      "reviewCount": sampleReviews.length.toString()
    },

    "review": sampleReviews.map((review) => ({
      "@type": "Review",

      "author": {
        "@type": "Person",
        "name": review.author_name || "Verified Customer"
      },

      // Replace relative text with real date
      "datePublished":
        review.datePublished || new Date().toISOString(),

      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": "5",
        "worstRating": "1"
      },

      "reviewBody": review.text,

      "itemReviewed": {
        "@type": "Service",
        "name": review.project || "Custom Kitchen Cabinet Installation",
        "description":
          "Premium kitchen cabinet installation and renovation services by Laminance Cabinetry"
      }
    }))
  }
};

  const aggregateRatingSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Laminance Cabinetry Kitchen Services",
    "description": "Custom kitchen cabinet design, installation, and complete renovation services in New Jersey.",
    "brand": {
      "@type": "Brand",
      "name": "Laminance Cabinetry"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "bestRating": "5",
      "ratingCount": totalReviews,
      "reviewCount": totalReviews
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "name": "Laminance Cabinetry",
    "description": "Premier custom kitchen cabinet and complete kitchen renovation services in New Jersey. 5-star rated with hundreds of satisfied customers.",
    "url": "https://laminance.com",
    "telephone": "+18624506069",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "418 Rt 23",
      "addressLocality": "Franklin",
      "addressRegion": "NJ",
      "postalCode": "07416",
      "addressCountry": "US"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "bestRating": "5",
      "ratingCount": totalReviews
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Laminance Cabinetry",
    "image": "https://laminance.com/assets/Images/logo.png",
    "priceRange": "$$",
    "telephone": "+18624506069",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "418 Rt 23",
      "addressLocality": "Franklin",
      "addressRegion": "NJ",
      "postalCode": "07416",
      "addressCountry": "US"
    },
    "review": sampleReviews.map(review => ({
      "@type": "Review",
      "author": review.author_name,
      "datePublished": review.relative_time_description,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": "5"
      },
      "reviewBody": review.text.substring(0, 200)
    }))
  };

  return (
    <>
      <Helmet>
        <title>Customer Testimonials & Reviews | 5-Star Kitchen Cabinet Services | Laminance Cabinetry</title>
        <meta name="title" content="Customer Testimonials & Reviews | 5-Star Kitchen Cabinet Services | Laminance Cabinetry" />
        <meta name="description" content="Read authentic 5-star customer reviews for Laminance Cabinetry's custom kitchen cabinet services. See why homeowners in New Jersey trust us for their kitchen transformations. Over {totalReviews}+ satisfied customers!" />
        <meta name="keywords" content="customer testimonials, kitchen cabinet reviews, Laminance Cabinetry reviews, 5-star kitchen services, kitchen renovation testimonials, New Jersey kitchen reviews" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://laminance.com/testimonials" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://laminance.com/testimonials" />
        <meta property="og:title" content="Customer Testimonials & Reviews | Laminance Cabinetry" />
        <meta property="og:description" content="Read authentic 5-star customer reviews for Laminance Cabinetry's custom kitchen cabinet services. {totalReviews}+ satisfied customers in New Jersey!" />
        <meta property="og:image" content="https://laminance.com/assets/Images/logo.png" />
        <meta property="og:image:alt" content="Laminance Cabinetry customer testimonials and 5-star reviews" />
        <meta property="og:site_name" content="Laminance Cabinetry" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Customer Testimonials & Reviews | Laminance Cabinetry" />
        <meta name="twitter:description" content={`Read authentic 5-star customer reviews. ${totalReviews}+ satisfied customers in New Jersey!`} />
        <meta name="twitter:image" content="https://laminance.com/assets/Images/logo.png" />
        
        {/* Geo Meta Tags */}
        <meta name="geo.region" content="US-NJ" />
        <meta name="geo.placename" content="Franklin" />
        <meta name="geo.position" content="41.1220;-74.5804" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(testimonialsSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(aggregateRatingSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <section 
        id="Testimonial-section"
        ref={sectionRef}
        className="py-12 md:py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
        style={{
          background: "var(--color-gray-50)",
        }}
        aria-labelledby="testimonials-heading"
      >
        {/* Hidden SEO Image */}
        <img 
          src="https://laminance.com/assets/Images/logo.png"
          alt="Laminance Cabinetry customer testimonials and 5-star reviews for kitchen cabinet services in New Jersey"
          className="hidden"
          loading="eager"
          fetchPriority="high"
          width="1200"
          height="630"
        />

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none" aria-hidden="true">
          <div 
            className="absolute top-1/4 left-1/4 w-48 md:w-64 h-48 md:h-64 rounded-full"
            style={{ background: "var(--color-primary-400)" }}
          ></div>
          <div 
            className="absolute bottom-1/4 right-1/4 w-48 md:w-64 h-48 md:h-64 rounded-full"
            style={{ background: "var(--color-primary-400)" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 mx-auto"
              style={{
                background: "var(--bg-primary-10)",
                border: "1px solid var(--border-primary-20)",
              }}
            >
              <MessageSquare size={14} style={{ color: "var(--color-primary-400)" }} />
              <span className="text-xs font-medium" style={{ color: "var(--color-primary-400)" }}>
                Client Testimonials
              </span>
            </div>
            
            <h1
              id="testimonials-heading"
              className="section-title text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 px-4"
              style={{
                background: "var(--gradient-primary)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Extremely High Standard of<br className="hidden sm:block" />Customer Satisfaction
            </h1>
            
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="flex items-center gap-1">
                {renderStars(5)}
              </div>
              <span className="text-sm font-semibold" style={{ color: "var(--color-gray-700)" }}>
                {averageRating} out of 5
              </span>
            </div>
            
            <p className="text-sm md:text-base max-w-2xl mx-auto" style={{ color: "var(--color-gray-600)" }}>
              Based on verified customer reviews. See what our clients have to say about their 
              Laminance kitchen transformations and custom cabinet installations in New Jersey.
            </p>
          </div>

          {/* Testimonials Marquee */}
          <div className="relative mb-12 md:mb-16">
            {/* Controls Info */}
            <div className="flex flex-wrap justify-center gap-2 mb-4">
              <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs"
                style={{
                  background: "var(--color-white)",
                  border: "1px solid var(--border-light)",
                }}
              >
                {isMobile ? <Fingerprint size={12} style={{ color: "var(--color-primary-400)" }} /> : <MousePointer size={12} style={{ color: "var(--color-primary-400)" }} />}
                <span style={{ color: "var(--color-gray-600)" }}>
                  {isMobile ? "Swipe to scroll" : "Drag to scroll"}
                </span>
              </div>
              
              <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs"
                style={{
                  background: "var(--color-white)",
                  border: "1px solid var(--border-light)",
                }}
              >
                <Hand size={12} style={{ color: "var(--color-primary-400)" }} />
                <span style={{ color: "var(--color-gray-600)" }}>
                  {isMobile ? "Tap to pause" : "Hover to pause"}
                </span>
              </div>
            </div>

            {/* Scrollable Container */}
            <div 
              ref={scrollContainerRef}
              className="overflow-x-auto overflow-y-hidden py-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
                cursor: isMobile ? 'grab' : 'default',
              }}
              onMouseEnter={() => {
                if (!isMobile) {
                  setIsHovering(true);
                  setIsAutoPlaying(false);
                }
              }}
              onMouseLeave={() => {
                if (!isMobile) {
                  setIsHovering(false);
                  setTimeout(() => setIsAutoPlaying(true), 2000);
                }
              }}
              onTouchStart={handleInteractionStart}
              onTouchEnd={handleInteractionEnd}
            >
              <motion.div 
                ref={marqueeRef}
                animate={!isMobile ? controls : {}}
                drag={!isMobile ? "x" : false}
                dragConstraints={scrollContainerRef}
                dragElastic={0.1}
                dragMomentum={true}
                onDragStart={handleInteractionStart}
                onDragEnd={handleInteractionEnd}
                className="flex gap-4 md:gap-6"
                style={{ width: 'max-content' }}
              >
                {duplicatedReviews.map((review, index) => {
                  const reviewKey = `${review.id}-${index}`;
                  
                  return (
                    <div 
                      key={reviewKey} 
                      className="w-[280px] md:w-80 flex-shrink-0"
                      itemScope
                      itemType="https://schema.org/Review"
                    >
                      <div 
                        className="relative p-4 md:p-5 rounded-xl md:rounded-2xl transition-all duration-300 h-full"
                        style={{
                          background: "var(--color-white)",
                          border: "1px solid var(--border-light)",
                          boxShadow: "var(--shadow-soft)",
                        }}
                      >
                        <meta itemProp="datePublished" content={review.relative_time_description} />
                        
                        {/* Rating Badge */}
                        <div className="absolute -top-2 -right-2">
                          <div className="flex items-center gap-1 px-2 py-1 rounded-full"
                            style={{
                              background: "var(--color-white)",
                              border: "1px solid var(--border-light)",
                              boxShadow: "var(--shadow-medium)",
                            }}
                          >
                            <div itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                              <meta itemProp="ratingValue" content={review.rating} />
                              <meta itemProp="bestRating" content="5" />
                              {renderStars(review.rating)}
                              <span className="text-xs font-medium ml-0.5" style={{ color: "var(--color-gray-700)" }}>
                                {review.rating}.0
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Review Content */}
                        <div className="mb-3 md:mb-4">
                          <Quote 
                            size={isMobile ? 18 : 20}
                            className="mb-2 opacity-40"
                            style={{ color: "var(--color-primary-400)" }}
                            aria-hidden="true"
                          />
                          <div itemProp="reviewBody">
                            <TruncatedText 
                              text={review.text} 
                              reviewId={reviewKey}
                            />
                          </div>
                        </div>

                        {/* Reviewer Info */}
                        <div className="flex items-center gap-2 pt-3 border-t"
                          style={{ borderColor: "var(--border-light)" }}
                        >
                          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden border-2 flex-shrink-0"
                            style={{ borderColor: "var(--color-white)" }}
                          >
                            <img 
                              src={review.profile_photo_url} 
                              alt={`${review.author_name} - verified customer review for Laminance Cabinetry`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              width="40"
                              height="40"
                              decoding="async"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h2 className="font-semibold text-sm truncate" style={{ color: "var(--color-gray-600)" }}>
                              <span itemProp="author">{review.author_name}</span>
                            </h2>
                            <p className="text-xs truncate" style={{ color: "var(--color-gray-500)" }}>
                              {review.relative_time_description}
                            </p>
                          </div>
                        </div>

                        {/* Project Info */}
                        {review.project && (
                          <div className="mt-2 text-xs px-2 py-1.5 rounded-lg truncate"
                            style={{
                              background: "var(--bg-primary-10)",
                              color: "var(--color-primary-400)",
                            }}
                          >
                            <span itemProp="itemReviewed">{review.project}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </motion.div>
            </div>

            {/* Gradient Overlays - Desktop only */}
            {!isMobile && (
              <>
                <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 pointer-events-none"
                  style={{
                    background: "linear-gradient(to right, var(--color-gray-50), transparent)",
                  }}
                  aria-hidden="true"
                ></div>
                <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 pointer-events-none"
                  style={{
                    background: "linear-gradient(to left, var(--color-gray-50), transparent)",
                  }}
                  aria-hidden="true"
                ></div>
              </>
            )}
          </div>

          {/* SEO Content Section */}
          <div className="mt-12 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="bell-mt-primary text-xl md:text-2xl font-bold mb-3" style={{ color: "var(--color-primary-500)" }}>
                Why New Jersey Homeowners Choose Laminance Cabinetry
              </h2>
              <p className="inter-secondary text-sm md:text-base mb-4" style={{ color: "var(--color-gray-600)" }}>
                With over 5-star reviews, Laminance Cabinetry has established itself as a trusted name 
                for custom kitchen cabinets and complete kitchen renovations in New Jersey. Our commitment to quality, 
                attention to detail, and customer satisfaction sets us apart.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: "var(--bg-primary-10)", color: "var(--color-primary-600)" }}>
                  5-Star Rated
                </span>
                <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: "var(--bg-primary-10)", color: "var(--color-primary-600)" }}>
                  Premium Quality
                </span>
                <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: "var(--bg-primary-10)", color: "var(--color-primary-600)" }}>
                  Professional Installation
                </span>
                <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: "var(--bg-primary-10)", color: "var(--color-primary-600)" }}>
                  Customer Satisfaction
                </span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-8 md:mt-12">
            <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ color: "var(--color-gray-600)" }}>
              Ready to Transform Your Kitchen?
            </h3>
            <p className="text-sm text-gray-600 mb-4 md:mb-6">
              Join hundreds of satisfied customers who trusted Laminance Cabinetry with their kitchen transformation.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <motion.a
                href={GOOGLE_MAPS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 md:px-6 py-2.5 md:py-3 rounded-lg font-semibold flex items-center justify-center gap-2 text-sm"
                style={{
                  background: "var(--color-primary-400)",
                  color: "var(--color-white)",
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label="Write a Google review for Laminance Cabinetry"
              >
                <ThumbsUp size={16} />
                <span>Write a Review</span>
                <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href={GOOGLE_MAPS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 md:px-6 py-2.5 md:py-3 rounded-lg font-semibold flex items-center justify-center gap-2 text-sm"
                style={{
                  background: "var(--color-white)",
                  border: "2px solid var(--color-primary-400)",
                  color: "var(--color-primary-400)",
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label="View all customer reviews for Laminance Cabinetry"
              >
                <span>View All Reviews</span>
              </motion.a>
            </div>

            <p className="mt-4 text-xs" style={{ color: "var(--color-gray-500)" }}>
              All reviews are verified from Google Business Profile | Serving all of New Jersey
            </p>
          </div>
        </div>
      </section>

      {/* CSS for hiding scrollbar */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        @media (max-width: 768px) {
          .overflow-x-auto {
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
          }
          
          .overflow-x-auto > div {
            scroll-snap-align: start;
          }
        }
      `}</style>
    </>
  );
};

export default TestimonialsSection;